/**
 * Pure Playwright trace.zip parser.
 *
 * No browser, no Playwright runtime — just adm-zip + JSON. Safe to call from any
 * Node/Bun process (worker server, CLI, tests).
 *
 * Lifted from packages/canary-cli/src/trace/trace-action-parser.ts so the
 * diagnostic materializer (apps/api) and the CLI use one parser.
 */

import AdmZip from 'adm-zip';
import type { TraceAction, TraceContext, TraceGroup, TraceViewport } from './types.js';

interface RawTraceEvent {
  type: string;
  callId?: string;
  title?: string;
  class?: string;
  method?: string;
  apiName?: string;
  startTime?: number;
  endTime?: number;
  wallTime?: number;
  params?: Record<string, unknown>;
  options?: Record<string, unknown>;
  beforeSnapshot?: string;
  afterSnapshot?: string;
  inputSnapshot?: string;
  pageId?: string;
  error?: { name: string; message: string };
  point?: { x: number; y: number };
  parentId?: string;
  message?: string;
  log?: unknown;
  logs?: unknown;
}

/** Methods that mutate page state (clicks, fills, navigation). */
const MUTATION_METHODS = new Set([
  'click',
  'dblclick',
  'fill',
  'type',
  'press',
  'check',
  'uncheck',
  'selectOption',
  'goto',
  'goBack',
  'goForward',
  'reload',
  'hover',
  'mouseClick',
  'mouseMove',
  'dragTo',
  'setInputFiles',
  'scrollIntoViewIfNeeded',
]);

const OBSERVATION_METHODS = new Set(['screenshot', 'accessibilitySnapshot']);

function deriveTitle(event: RawTraceEvent): string {
  return (
    event.apiName ??
    event.title ??
    (event.class && event.method
      ? `${event.class.toLowerCase()}.${event.method}`
      : event.method ?? 'unknown')
  );
}

function getTraceEntries(zip: AdmZip) {
  return zip
    .getEntries()
    .filter((e) => e.entryName.endsWith('.trace') && !e.entryName.endsWith('.network'));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readViewport(value: unknown): TraceViewport | undefined {
  if (!isRecord(value)) return undefined;
  const width = value.width;
  const height = value.height;
  return typeof width === 'number' && typeof height === 'number' ? { width, height } : undefined;
}

function readContextViewport(event: RawTraceEvent): TraceViewport | undefined {
  return readViewport(event.options?.viewport);
}

function appendLogLines(value: unknown, out: string[]): void {
  if (typeof value === 'string') {
    out.push(value);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) appendLogLines(item, out);
    return;
  }
  if (!isRecord(value)) return;
  const message = value.message;
  if (typeof message === 'string') out.push(message);
  const text = value.text;
  if (typeof text === 'string') out.push(text);
}

function extractLogLines(event: RawTraceEvent): string[] {
  const lines: string[] = [];
  if (typeof event.message === 'string') lines.push(event.message);
  appendLogLines(event.params?.message, lines);
  appendLogLines(event.log, lines);
  appendLogLines(event.logs, lines);
  return lines;
}

const RESOLVED_ELEMENT_MARKER = 'locator resolved to ';

function latestResolvedElementPreview(event: RawTraceEvent): string | undefined {
  let latest: string | undefined;
  for (const line of extractLogLines(event)) {
    const idx = line.indexOf(RESOLVED_ELEMENT_MARKER);
    // Store only the element preview itself — consumers parse it as a
    // tag/name/text signal, and the log-line prefix would misparse as a
    // `locator` tag that conflicts with every element.
    if (idx !== -1) latest = line.slice(idx + RESOLVED_ELEMENT_MARKER.length).trim();
  }
  return latest;
}

function applyResolvedElementPreview(
  callId: string,
  preview: string,
  previewByCallId: Map<string, string>,
  actionMap: Map<string, Partial<TraceAction>>
): void {
  previewByCallId.set(callId, preview);
  const existing = actionMap.get(callId);
  if (existing) existing.resolvedElementPreview = preview;
}

/**
 * Parse context-level trace metadata. Never throws: unreadable archives, missing
 * `.trace` entries, and malformed lines return an empty context.
 */
export function parseTraceContext(traceZipPath: string): TraceContext {
  let zip: AdmZip;
  try {
    zip = new AdmZip(traceZipPath);
  } catch {
    return {};
  }
  const traceEntries = getTraceEntries(zip);

  if (traceEntries.length === 0) return {};

  const viewports: TraceViewport[] = [];
  for (const entry of traceEntries) {
    let content: string;
    try {
      content = entry.getData().toString('utf-8');
    } catch {
      continue;
    }
    for (const line of content.split('\n')) {
      if (!line.trim()) continue;
      let event: RawTraceEvent;
      try {
        event = JSON.parse(line);
      } catch {
        continue;
      }
      if (event.type !== 'context-options') continue;
      const viewport = readContextViewport(event);
      if (viewport) viewports.push(viewport);
    }
  }

  const first = viewports[0];
  if (!first) return {};
  const viewportDisagreement = viewports.some((v) => v.width !== first.width || v.height !== first.height);
  return {
    viewport: first,
    ...(viewportDisagreement ? { viewportDisagreement: true } : {}),
  };
}

/**
 * Parse a Playwright trace.zip and extract the list of user actions
 * with their associated snapshot names.
 *
 * Merges 'before', 'input', 'after' events by callId (same merging
 * logic as Playwright's traceModernizer).
 */
export function parseTraceActions(traceZipPath: string): TraceAction[] {
  const zip = new AdmZip(traceZipPath);
  const traceEntries = getTraceEntries(zip);

  if (traceEntries.length === 0) {
    throw new Error('No .trace entries found in the zip archive');
  }

  const actionMap = new Map<string, Partial<TraceAction>>();
  const resolvedPreviewByCallId = new Map<string, string>();

  for (const entry of traceEntries) {
    const content = entry.getData().toString('utf-8');
    for (const line of content.split('\n')) {
      if (!line.trim()) continue;
      let event: RawTraceEvent;
      try {
        event = JSON.parse(line);
      } catch {
        continue;
      }

      if (!event.callId) continue;

      const resolvedPreview = latestResolvedElementPreview(event);
      if (resolvedPreview) {
        applyResolvedElementPreview(event.callId, resolvedPreview, resolvedPreviewByCallId, actionMap);
      }

      switch (event.type) {
        case 'before': {
          actionMap.set(event.callId, {
            callId: event.callId,
            title: deriveTitle(event),
            startTime: event.startTime ?? 0,
            endTime: 0,
            wallTime: event.wallTime,
            params: event.params,
            beforeSnapshot: event.beforeSnapshot,
            pageId: event.pageId ?? '',
            point: event.point,
            parentId: event.parentId,
            resolvedElementPreview: resolvedPreviewByCallId.get(event.callId),
          });
          break;
        }
        case 'input': {
          const existing = actionMap.get(event.callId);
          if (existing) {
            existing.inputSnapshot = event.inputSnapshot;
            if (event.point) existing.point = event.point;
          }
          break;
        }
        case 'after': {
          const existing = actionMap.get(event.callId);
          if (existing) {
            existing.afterSnapshot = event.afterSnapshot;
            existing.endTime = event.endTime ?? existing.endTime ?? 0;
            existing.error = event.error;
            if (event.point) existing.point = event.point;
          }
          break;
        }
        case 'action': {
          actionMap.set(event.callId, {
            callId: event.callId,
            title: deriveTitle(event),
            startTime: event.startTime ?? 0,
            endTime: event.endTime ?? 0,
            wallTime: event.wallTime,
            params: event.params,
            beforeSnapshot: event.beforeSnapshot,
            afterSnapshot: event.afterSnapshot,
            inputSnapshot: event.inputSnapshot,
            pageId: event.pageId ?? '',
            error: event.error,
            point: event.point,
            parentId: event.parentId,
            resolvedElementPreview: resolvedPreviewByCallId.get(event.callId),
          });
          break;
        }
      }
    }
  }

  const actions = [...actionMap.values()].filter((a) => {
    if (!a.callId || !(a.beforeSnapshot || a.afterSnapshot)) return false;
    const method = a.title?.split('.').pop() ?? '';
    return MUTATION_METHODS.has(method) || OBSERVATION_METHODS.has(method);
  }) as TraceAction[];

  // Order globally. A stitched multi-segment trace contains several browser
  // processes whose monotonic `startTime` clocks each restart near 0, so sorting
  // on startTime interleaves segments. `wallTime` is absolute and segment-stable.
  // Use it ONLY when every action has it (uniform key) — mixing wallTime (~1e12)
  // with a startTime fallback (~hundreds) would sort the fallback rows to the
  // front. When any is missing, fall back wholesale to startTime (single-segment
  // behaviour, unchanged).
  const sortByWallTime = actions.every((a) => typeof a.wallTime === 'number');
  actions.sort((a, b) =>
    sortByWallTime
      ? (a.wallTime ?? 0) - (b.wallTime ?? 0)
      : (a.startTime ?? 0) - (b.startTime ?? 0)
  );

  return actions;
}

/**
 * Filter to only mutation actions (clicks, fills, navigation).
 * Excludes observation actions (screenshots, accessibility snapshots).
 */
export function filterMutationActions(actions: TraceAction[]): TraceAction[] {
  return actions.filter((a) => {
    const method = a.title?.split('.').pop() ?? '';
    return !OBSERVATION_METHODS.has(method);
  });
}

export type MutationIndexResolution =
  | { ok: true; mutationIndex: number }
  | { ok: false; reason: string };

/**
 * Resolve a `parseTraceActions` index (the order `browser/actions.jsonl` and
 * `optimizer/segments.json` use) to the action's position in the
 * mutation-filtered list `reconstructSnapshots` indexes.
 *
 * Two index spaces exist because the reconstructor predates the optimizer
 * workspace artifacts; every optimizer-facing surface speaks jsonl order and
 * remaps here. An observation action (screenshot/a11y snapshot) has no
 * mutation position — the failure names the neighbouring mutation indices so
 * an agent can self-correct.
 */
export function toMutationIndex(
  allActions: TraceAction[],
  actionIndex: number
): MutationIndexResolution {
  if (!Number.isInteger(actionIndex) || actionIndex < 0 || actionIndex >= allActions.length) {
    return {
      ok: false,
      reason: `Action index ${actionIndex} out of range (0-${allActions.length - 1} in actions.jsonl order)`,
    };
  }
  const target = allActions[actionIndex]!;
  const mutationPositions = new Map(
    filterMutationActions(allActions).map((a, i) => [a.callId, i])
  );
  const mutationIndex = mutationPositions.get(target.callId);
  if (mutationIndex === undefined) {
    let prev: number | null = null;
    let next: number | null = null;
    for (let i = actionIndex - 1; i >= 0 && prev === null; i--) {
      if (mutationPositions.has(allActions[i]!.callId)) prev = i;
    }
    for (let i = actionIndex + 1; i < allActions.length && next === null; i++) {
      if (mutationPositions.has(allActions[i]!.callId)) next = i;
    }
    const neighbours = [
      prev !== null ? `previous mutation at index ${prev}` : null,
      next !== null ? `next mutation at index ${next}` : null,
    ]
      .filter(Boolean)
      .join(', ');
    return {
      ok: false,
      reason:
        `Action ${actionIndex} (${target.title}) is an observation action with no page state of its own — ` +
        `materialize a mutation action instead${neighbours ? ` (${neighbours})` : ''}`,
    };
  }
  return { ok: true, mutationIndex };
}

/**
 * Parse the named `tracing.group()` spans out of a Playwright trace.zip.
 *
 * Groups are recorded as `before` events with `method: "tracingGroup"` and the
 * group name as `title`; `tracing.groupEnd()` writes the matching `after`
 * event. A group whose `after` never arrived (run failed mid-group) has no
 * `endTime` — callers should treat it as open-ended.
 */
export function parseTraceGroups(traceZipPath: string): TraceGroup[] {
  const zip = new AdmZip(traceZipPath);
  const traceEntries = getTraceEntries(zip);

  if (traceEntries.length === 0) {
    throw new Error('No .trace entries found in the zip archive');
  }

  const groups = new Map<string, TraceGroup>();

  for (const entry of traceEntries) {
    const content = entry.getData().toString('utf-8');
    for (const line of content.split('\n')) {
      if (!line.trim()) continue;
      let event: RawTraceEvent;
      try {
        event = JSON.parse(line);
      } catch {
        continue;
      }
      if (!event.callId) continue;

      if (event.type === 'before' && event.method === 'tracingGroup') {
        groups.set(event.callId, {
          callId: event.callId,
          name: event.title ?? '',
          startTime: event.startTime ?? 0,
          wallTime: event.wallTime,
          parentId: event.parentId,
        });
      } else if (event.type === 'after') {
        const group = groups.get(event.callId);
        if (group) group.endTime = event.endTime;
      }
    }
  }

  // Same global-ordering concern as parseTraceActions: prefer the segment-stable
  // wallTime, but only as a uniform key (all-or-nothing) to avoid mixing scales.
  const groupList = [...groups.values()];
  const sortByWallTime = groupList.every((g) => typeof g.wallTime === 'number');
  return groupList.sort((a, b) =>
    sortByWallTime ? (a.wallTime ?? 0) - (b.wallTime ?? 0) : a.startTime - b.startTime
  );
}
